<template>
  <a-modal
    :visible="visible"
    title="添加农场日产记录"
    @cancel="on_close"
    @ok="on_submit"
    :width="600"
    okText="提交"
    cancelText="取消"
    :confirmLoading="loading"
  >
    <a-form-model
      ref="form"
      :model="form"
      :rules="rules"
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item label="记录日期" prop="record_date">
        <a-date-picker
          v-model="form.record_date"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          style="width: 100%"
          placeholder="请选择日期"
        />
      </a-form-model-item>

      <a-form-model-item label="总产量(kg)" prop="total_production">
        <a-input-number
          v-model="form.total_production"
          :min="0"
          :precision="2"
          style="width: 100%"
          placeholder="请输入总产量"
        />
      </a-form-model-item>

      <a-form-model-item label="乳脂率(%)" prop="fat_rate">
        <a-input-number
          v-model="form.fat_rate"
          :min="0"
          :max="100"
          :precision="2"
          style="width: 100%"
          placeholder="选填"
        />
      </a-form-model-item>

      <a-form-model-item label="干物质(kg)" prop="dry_mass">
        <a-input-number
          v-model="form.dry_mass"
          :min="0"
          :precision="2"
          style="width: 100%"
          placeholder="选填"
        />
      </a-form-model-item>

      <a-form-model-item label="剔除个体">
        <div>
          <a-button type="dashed" @click="open_item_select" style="margin-bottom: 8px">
            <a-icon type="plus" /> 选择未生产个体
          </a-button>
          <div class="excluded-items-container" v-if="excluded_items.length > 0">
            <a-tag
              v-for="item in excluded_items"
              :key="item.id"
              closable
              @close="remove_excluded_item(item)"
              color="orange"
            >
              {{ item.vid || item.id }}
            </a-tag>
          </div>
          <div v-else style="color: #999; font-size: 12px">
            点击上方按钮选择当日未参与生产的个体
          </div>
        </div>
      </a-form-model-item>

      <a-form-model-item label="生产个体数">
        <span>{{ producing_item_count }} 只</span>
        <span style="color: #999; font-size: 12px; margin-left: 8px">
          (总存栏 {{ total_item_count }} - 剔除 {{ excluded_items.length }})
        </span>
      </a-form-model-item>

      <a-form-model-item label="平均每只产量">
        <span>{{ avg_weight_per_item }} kg</span>
      </a-form-model-item>
    </a-form-model>

    <item-multi-select
      ref="itemMultiSelect"
      @select-over="on_item_select"
    />
  </a-modal>
</template>

<script>
import ItemMultiSelect from "./itemMultiSelect.vue";

export default {
  name: "AddDailyProductionRecord",
  components: {
    ItemMultiSelect,
  },
  data() {
    return {
      visible: false,
      loading: false,
      farm_id: null,
      total_item_count: 0,
      excluded_items: [],
      form: {
        record_date: null,
        total_production: null,
        fat_rate: null,
        dry_mass: null,
      },
      rules: {
        record_date: [
          { required: true, message: "请选择记录日期", trigger: "change" },
        ],
        total_production: [
          { required: true, message: "请输入总产量", trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    producing_item_count() {
      return Math.max(0, this.total_item_count - this.excluded_items.length);
    },
    avg_weight_per_item() {
      if (this.producing_item_count > 0 && this.form.total_production) {
        return (this.form.total_production / this.producing_item_count).toFixed(2);
      }
      return "0.00";
    },
  },
  methods: {
    async open(options) {
      this.visible = true;
      this.farm_id = options.farm_id;
      this.excluded_items = [];
      this.form = {
        record_date: this.get_today(),
        total_production: null,
        fat_rate: null,
        dry_mass: null,
      };
      // 获取农场总存栏数
      await this.load_farm_item_count();
    },
    get_today() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async load_farm_item_count() {
      try {
        const res = await this.$api.get_breed_items({
          farm_id: this.farm_id,
          page_no: 1,
          page_size: 1,
        });
        this.total_item_count = res.data.page.total || 0;
      } catch (error) {
        console.error("获取农场存栏数失败:", error);
        this.total_item_count = 0;
      }
    },
    open_item_select() {
      this.$refs.itemMultiSelect.open({
        farm_id: this.farm_id,
      });
    },
    on_item_select(selectedRows) {
      // 合并已选择的个体，避免重复
      const existingIds = this.excluded_items.map((item) => item.id);
      const newItems = selectedRows.filter(
        (item) => !existingIds.includes(item.id)
      );
      this.excluded_items = [...this.excluded_items, ...newItems];
    },
    remove_excluded_item(item) {
      this.excluded_items = this.excluded_items.filter((i) => i.id !== item.id);
    },
    on_submit() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          this.loading = true;
          try {
            const data = {
              farm_id: this.farm_id,
              record_date: this.form.record_date,
              total_production: this.form.total_production,
              excluded_item_ids: this.excluded_items.map((item) => item.id),
              fat_rate: this.form.fat_rate,
              dry_mass: this.form.dry_mass,
            };

            const res = await this.$api.addManualRecord(data);
            if (res.code === 200) {
              this.$message.success("添加日产记录成功");
              this.$emit("success");
              this.on_close();
            } else {
              this.$message.error(res.message || "添加失败");
            }
          } catch (error) {
            this.$message.error(error.message || "添加失败");
          } finally {
            this.loading = false;
          }
        }
      });
    },
    on_close() {
      this.visible = false;
      this.excluded_items = [];
      this.form = {
        record_date: null,
        total_production: null,
        fat_rate: null,
        dry_mass: null,
      };
      if (this.$refs.form) {
        this.$refs.form.resetFields();
      }
    },
  },
};
</script>

<style scoped>
.excluded-items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
</style>
